// redux/slices/productUpdatesSlice.js (web)
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "../../Utils/apiClient";
import { resetStore } from "./resetStore";

const errorText = (error) =>
  error?.response?.data?.error || error?.message || "Request failed";

const call = async (method, path, data) =>
  (await apiRequest(method, `/product-updates${path}`, data))?.data;

const thunk = (name, handler) =>
  createAsyncThunk(`productUpdates/${name}`, async (arg, { rejectWithValue }) => {
    try {
      return await handler(arg);
    } catch (error) {
      return rejectWithValue(errorText(error));
    }
  });

export const fetchProductUpdates = thunk("fetch", () => call("GET", "/"));
export const markProductUpdateRead = thunk("read", (id) => call("PUT", `/${id}/read`));
export const voteProductUpdate = thunk("vote", ({ id, vote }) => call("PUT", `/${id}/vote`, { vote }));
export const hideProductUpdates = thunk("hideUpdates", () =>
  call("PUT", "/updates-preference", { hidden: true }));

const initialState = {
  items: [],
  status: "idle",
  error: null,
  pendingVotes: {},
  replayUntil: null,
  updatesHidden: false,
};

const productUpdatesSlice = createSlice({
  name: "productUpdates",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductUpdates.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchProductUpdates.fulfilled, (state, action) => {
        state.items = action.payload?.items || [];
        state.replayUntil = action.payload?.replayUntil || null;
        state.updatesHidden = !!action.payload?.updatesHidden;
        state.status = "succeeded";
      })
      .addCase(fetchProductUpdates.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(markProductUpdateRead.fulfilled, (state, action) => {
        const item = state.items.find((v) => v.id === action.payload?.id);
        if (item) item.isRead = true;
      })
      .addCase(voteProductUpdate.pending, (state, action) => {
        state.pendingVotes[action.meta.arg.id] = true;
      })
      .addCase(voteProductUpdate.fulfilled, (state, action) => {
        const item = state.items.find((v) => v.id === action.payload?.id);
        if (item) Object.assign(item, action.payload);
        state.pendingVotes[action.meta.arg.id] = false;
      })
      .addCase(voteProductUpdate.rejected, (state, action) => {
        state.pendingVotes[action.meta.arg.id] = false;
        state.error = action.payload;
      })
      .addCase(hideProductUpdates.fulfilled, (state) => {
        state.updatesHidden = true;
      })
      .addCase(resetStore, () => ({ ...initialState, pendingVotes: {} }));
  },
});

export const selectProductUpdates = (state) => state.productUpdates;
export default productUpdatesSlice.reducer;
